import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Calendar, Star, CreditCard, XCircle, Clock, Share2, X, MessageSquare } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const RideHistory = () => {
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingRideId, setCancellingRideId] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedRideForRating, setSelectedRideForRating] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [tip, setTip] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchRides = async () => {
      try {
        const api = (await import('../utils/api')).default;
        const response = await api.get('/rides/my-rides');
        setRides(response.data.rides);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching rides:', error);
        setLoading(false);
      }
    };

    fetchRides();
  }, [navigate]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      accepted: { color: 'bg-blue-100 text-blue-800', label: 'Accepted' },
      arriving: { color: 'bg-purple-100 text-purple-800', label: 'Arriving' },
      in_progress: { color: 'bg-green-100 text-green-800', label: 'In Progress' },
      completed: { color: 'bg-gray-100 text-gray-800', label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Clock className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  const getPaymentBadge = (paymentStatus) => {
    if (!paymentStatus) return null;
    const config = {
      initiated: { color: 'bg-yellow-100 text-yellow-800', label: 'Payment Pending' },
      paid: { color: 'bg-green-100 text-green-800', label: 'Paid' },
      failed: { color: 'bg-red-100 text-red-800', label: 'Payment Failed' },
      expired: { color: 'bg-gray-100 text-gray-800', label: 'Payment Expired' },
    };
    const badge = config[paymentStatus] || config.initiated;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <CreditCard className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const canCancelRide = (status) => {
    return ['pending', 'accepted'].includes(status);
  };

  const handleCancelRide = async (rideId) => {
    if (!window.confirm('Are you sure you want to cancel this ride?')) {
      return;
    }

    setCancellingRideId(rideId);
    try {
      const api = (await import('../utils/api')).default;
      await api.put(`/rides/${rideId}/cancel`, { reason: 'User cancelled' });
      
      toast({
        title: 'Ride cancelled',
        description: 'Your ride has been cancelled successfully.',
      });

      // Refresh the rides list
      const response = await api.get('/rides/my-rides');
      setRides(response.data.rides);
    } catch (error) {
      console.error('Error cancelling ride:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to cancel ride',
        variant: 'destructive',
      });
    } finally {
      setCancellingRideId(null);
    }
  };

  const handleShareReceipt = (ride) => {
    setSelectedReceipt(ride);
    setShowReceiptModal(true);
  };

  const handleDownloadPdfReceipt = async (ride) => {
    try {
      const api = (await import('../utils/api')).default;
      const res = await api.get(`/rides/${ride.id}/receipt`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tekstar-receipt-${(ride.id || '').slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to download', variant: 'destructive' });
    }
  };

  const handleEmailReceipt = async (ride) => {
    try {
      const api = (await import('../utils/api')).default;
      const { data } = await api.post(`/rides/${ride.id}/receipt/email`);
      toast({ title: 'Receipt sent', description: data.to ? `Sent to ${data.to}` : 'Check your email' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to send', variant: 'destructive' });
    }
  };

  const generateReceiptText = (ride) => {
    const date = formatDate(ride.completedAt || ride.timestamp);
    return `🚗 Tekstar Ride Receipt

📍 From: ${ride.pickup}
📍 To: ${ride.dropoff}
📅 Date: ${date}
💰 Fare: K${ride.price}
${ride.tip > 0 ? `💝 Tip: K${ride.tip}` : ''}

Thanks for riding with Tekstar! 🌟
Book your ride: ${window.location.origin}`;
  };

  const shareViaWhatsApp = (ride) => {
    const text = encodeURIComponent(generateReceiptText(ride));
    window.open(`https://wa.me/?text=${text}`, '_blank');
    toast({ title: 'Opening WhatsApp...', description: 'Share your receipt with friends!' });
  };

  const shareViaSMS = (ride) => {
    const text = encodeURIComponent(generateReceiptText(ride));
    window.open(`sms:?body=${text}`, '_blank');
    toast({ title: 'Opening SMS...', description: 'Share your receipt via text!' });
  };

  const copyToClipboard = async (ride) => {
    try {
      await navigator.clipboard.writeText(generateReceiptText(ride));
      toast({ title: 'Copied!', description: 'Receipt copied to clipboard' });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to copy', variant: 'destructive' });
    }
  };

  const shareNative = async (ride) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Tekstar Ride Receipt',
          text: generateReceiptText(ride),
          url: window.location.origin,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(ride);
        }
      }
    } else {
      copyToClipboard(ride);
    }
  };

  // Rating functions
  const handleRateRide = (ride) => {
    setSelectedRideForRating(ride);
    setRating(0);
    setHoveredRating(0);
    setFeedback('');
    // Only allow tips for non-cash payments
    setTip(ride.paymentMethod === 'cash' ? 0 : 0);
    setShowRatingModal(true);
  };

  const submitRating = async () => {
    if (rating === 0) {
      toast({
        title: 'Rating required',
        description: 'Please select a star rating',
        variant: 'destructive',
      });
      return;
    }

    setSubmittingRating(true);
    try {
      const api = (await import('../utils/api')).default;
      await api.post(`/rides/${selectedRideForRating.id}/rate`, {
        rating,
        feedback,
        tip,
      });

      toast({
        title: 'Thank you!',
        description: 'Your rating has been submitted',
      });

      // Update local state
      setRides(rides.map(r => 
        r.id === selectedRideForRating.id 
          ? { ...r, rating, feedback } 
          : r
      ));

      setShowRatingModal(false);
      setSelectedRideForRating(null);
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to submit rating',
        variant: 'destructive',
      });
    } finally {
      setSubmittingRating(false);
    }
  };

  const getRatingLabel = (r) => {
    switch(r) {
      case 5: return 'Excellent!';
      case 4: return 'Great!';
      case 3: return 'Good';
      case 2: return 'Okay';
      case 1: return 'Poor';
      default: return '';
    }
  };

  const tipOptions = [2, 5, 10, 15];

  // Rating Modal Component
  const RatingModal = ({ ride, onClose }) => {
    if (!ride) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-6 rounded-t-2xl text-white">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">Rate Your Ride</h2>
                <p className="text-yellow-100 text-sm mt-1">How was your trip?</p>
              </div>
              <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          {/* Rating Content */}
          <div className="p-6 space-y-6">
            {/* Trip Summary */}
            <div className="text-center text-sm text-gray-600 pb-4 border-b">
              <p className="font-medium text-gray-900">{ride.pickup}</p>
              <p className="text-xs my-1">to</p>
              <p className="font-medium text-gray-900">{ride.dropoff}</p>
            </div>

            {/* Star Rating */}
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">Tap to rate</p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="transition-transform hover:scale-110 focus:outline-none"
                    data-testid={`rating-star-${star}`}
                  >
                    <Star
                      className={`h-10 w-10 transition-colors ${
                        star <= (hoveredRating || rating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="mt-2 text-lg font-medium text-gray-700">
                  {getRatingLabel(rating)}
                </p>
              )}
            </div>

            {/* Feedback */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Feedback (optional)
              </label>
              <Textarea
                placeholder="Tell us about your experience..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="min-h-20 resize-none"
                data-testid="rating-feedback"
              />
            </div>

            {/* Tip Options - Only show for non-cash payments */}
            {ride.driver && ride.paymentMethod !== 'cash' && (
              <div className="space-y-3">
                <label className="text-sm font-medium text-gray-700">
                  Add a tip for {ride.driver?.name || 'your driver'}?
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {tipOptions.map((amount) => (
                    <Button
                      key={amount}
                      variant={tip === amount ? 'default' : 'outline'}
                      size="sm"
                      className={tip === amount ? 'bg-green-600 hover:bg-green-700' : ''}
                      onClick={() => setTip(amount)}
                      data-testid={`tip-${amount}`}
                    >
                      K{amount}
                    </Button>
                  ))}
                  <Button
                    variant={tip === 0 ? 'default' : 'outline'}
                    size="sm"
                    className={tip === 0 ? 'bg-gray-500 hover:bg-gray-600' : ''}
                    onClick={() => setTip(0)}
                  >
                    None
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {/* Submit Button */}
          <div className="p-6 pt-2 border-t">
            <Button 
              onClick={submitRating}
              disabled={rating === 0 || submittingRating}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white py-6 text-lg font-semibold"
              data-testid="submit-rating-btn"
            >
              {submittingRating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Star className="h-5 w-5 mr-2" />
                  Submit Rating {tip > 0 && ride.paymentMethod !== 'cash' && `+ K${tip} Tip`}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Receipt Modal Component
  const ReceiptModal = ({ ride, onClose }) => {
    if (!ride) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-gradient-to-r from-pink-500 to-pink-600 p-6 rounded-t-2xl text-white">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">Ride Receipt</h2>
                <p className="text-pink-100 text-sm mt-1">Tekstar</p>
              </div>
              <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
          
          {/* Receipt Content */}
          <div className="p-6 space-y-4">
            {/* Date */}
            <div className="text-center text-gray-600 text-sm border-b pb-4">
              {formatDate(ride.completedAt || ride.timestamp)}
            </div>
            
            {/* Route */}
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-3 h-3 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                <div>
                  <p className="text-xs text-gray-500">PICKUP</p>
                  <p className="font-medium text-gray-900">{ride.pickup}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-3 h-3 rounded-full bg-pink-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <p className="text-xs text-gray-500">DROPOFF</p>
                  <p className="font-medium text-gray-900">{ride.dropoff}</p>
                </div>
              </div>
            </div>
            
            {/* Fare Breakdown */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Distance</span>
                <span className="font-medium">{ride.distance?.toFixed(1) || '-'} km</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Base Fare</span>
                <span className="font-medium">K{ride.baseFare?.toFixed(2) || ride.price}</span>
              </div>
              {ride.stopsFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Stops Fee</span>
                  <span className="font-medium">K{ride.stopsFee?.toFixed(2)}</span>
                </div>
              )}
              {ride.tip > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tip</span>
                  <span className="font-medium text-green-600">K{ride.tip?.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t">
                <span>Total</span>
                <span className="text-pink-600">K{(ride.price + (ride.tip || 0)).toFixed(2)}</span>
              </div>
            </div>
            
            {/* Payment Method */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Payment</span>
              <span className="font-medium capitalize">{ride.paymentMethod || 'Cash'}</span>
            </div>
            
            {/* Driver Info */}
            {ride.driver && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <img 
                  src={ride.driver.photo || '/default-avatar.png'} 
                  alt={ride.driver.name}
                  className="w-12 h-12 rounded-full bg-gray-200"
                />
                <div className="flex-1">
                  <p className="font-medium">{ride.driver.name}</p>
                  <p className="text-sm text-gray-500">{ride.rideType?.name || 'Standard'}</p>
                </div>
                {ride.rating && (
                  <div className="flex items-center gap-1 bg-yellow-100 px-2 py-1 rounded-full">
                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    <span className="text-sm font-medium">{ride.rating}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Share Actions */}
          <div className="p-6 pt-2 space-y-3 border-t">
            <p className="text-sm text-gray-600 text-center mb-3">Share this receipt</p>
            <div className="grid grid-cols-2 gap-3">
              <Button 
                onClick={() => shareViaWhatsApp(ride)}
                className="bg-green-500 hover:bg-green-600 text-white"
                data-testid="share-whatsapp-btn"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </Button>
              <Button 
                onClick={() => shareViaSMS(ride)}
                variant="outline"
                data-testid="share-sms-btn"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                SMS
              </Button>
            </div>
            <Button 
              onClick={() => shareNative(ride)}
              className="w-full bg-pink-600 hover:bg-pink-700 text-white"
              data-testid="share-native-btn"
            >
              <Share2 className="w-5 h-5 mr-2" />
              Share Receipt
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Rides</h1>
          <p className="text-gray-600">View your ride history</p>
        </div>

        {loading ? (
          <Card className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your rides...</p>
          </Card>
        ) : rides.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full">
                <MapPin className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">No rides yet</h3>
              <p className="text-gray-600">Book your first ride to get started</p>
              <Button
                onClick={() => navigate('/')}
                className="bg-pink-600 hover:bg-pink-700 text-white mt-4"
              >
                Book a ride
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {rides.map((ride, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-3">
                    {/* Date & Status */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="h-4 w-4" />
                        <span>{formatDate(ride.completedAt || ride.timestamp)}</span>
                      </div>
                      {getStatusBadge(ride.status)}
                      {ride.payment_status && getPaymentBadge(ride.payment_status)}
                    </div>

                    {/* Locations */}
                    <div className="space-y-2">
                      <div className="flex items-start gap-3">
                        <div className="w-3 h-3 rounded-full bg-gray-400 mt-1"></div>
                        <div>
                          <p className="text-sm text-gray-600">Pickup</p>
                          <p className="font-medium text-gray-900">{ride.pickup}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-3 h-3 rounded-full bg-pink-600 mt-1"></div>
                        <div>
                          <p className="text-sm text-gray-600">Dropoff</p>
                          <p className="font-medium text-gray-900">{ride.dropoff}</p>
                        </div>
                      </div>
                    </div>

                    {/* Driver & Rating */}
                    {ride.driver && (
                      <div className="flex items-center gap-3 pt-2">
                        <img 
                          src={ride.driver.photo} 
                          alt={ride.driver.name}
                          className="w-10 h-10 rounded-full bg-gray-200"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{ride.driver.name}</p>
                          <p className="text-sm text-gray-600">{ride.rideType.name}</p>
                        </div>
                        {ride.rating && (
                          <div className="ml-auto flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            <span className="text-sm font-medium">{ride.rating}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Price & Actions */}
                  <div className="text-right ml-4 space-y-2">
                    <p className="text-2xl font-bold text-gray-900">K{ride.price}</p>
                    {ride.tip > 0 && (
                      <p className="text-sm text-gray-600">+${ride.tip} tip</p>
                    )}
                    {canCancelRide(ride.status) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancelRide(ride.id)}
                        disabled={cancellingRideId === ride.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      >
                        {cancellingRideId === ride.id ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600 mr-2"></div>
                            Cancelling...
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 mr-1" />
                            Cancel Ride
                          </>
                        )}
                      </Button>
                    )}
                    {ride.status === 'completed' && !ride.rating && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRateRide(ride)}
                        className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 border-yellow-300 font-medium"
                        data-testid={`rate-ride-btn-${ride.id}`}
                      >
                        <Star className="h-4 w-4 mr-1" />
                        Rate Ride
                      </Button>
                    )}
                    {ride.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleShareReceipt(ride)}
                        className="text-pink-600 hover:text-pink-700 hover:bg-pink-50 border-pink-200"
                        data-testid={`share-receipt-btn-${ride.id}`}
                      >
                        <Share2 className="h-4 w-4 mr-1" />
                        Share Receipt
                      </Button>
                    )}
                    {ride.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadPdfReceipt(ride)}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        data-testid={`download-receipt-btn-${ride.id}`}
                      >
                        Download PDF
                      </Button>
                    )}
                    {ride.status === 'completed' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEmailReceipt(ride)}
                        className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        data-testid={`email-receipt-btn-${ride.id}`}
                      >
                        Email me
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Receipt Modal */}
      {showReceiptModal && (
        <ReceiptModal 
          ride={selectedReceipt} 
          onClose={() => {
            setShowReceiptModal(false);
            setSelectedReceipt(null);
          }} 
        />
      )}

      {/* Rating Modal */}
      {showRatingModal && (
        <RatingModal 
          ride={selectedRideForRating} 
          onClose={() => {
            setShowRatingModal(false);
            setSelectedRideForRating(null);
          }} 
        />
      )}
    </div>
  );
};

export default RideHistory;